import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useFirebaseMessaging,
  NotificationData,
} from "../hooks/useFirebaseMessaging";
import { useToast } from "../context/ToastContext";
import { isAndroid, isMobileDevice, isChrome } from "../services/mobileUtils";
import { useAuth } from "../features/auth/hooks/useAuth";
import { useActiveChat } from "../context/ActiveChatContext";
import {
  XMarkIcon,
  BellIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { FAQ_ROUTE } from "../routes";
import { resolveNotificationPath } from "../utils/notificationDeepLink";

const BANNER_DISMISSED_KEY = "notification_banner_dismissed_until";
const INSTRUCTIONS_VIEWED_KEY = "notification_instructions_viewed";
const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

export const FirebaseNotificationWrapper: React.FC = () => {
  const { isAuthenticated, identityId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { notification, requestPushPermission } = useFirebaseMessaging();
  const { showToast, hideToast } = useToast();
  // Properly typed ref for the previous notification: avoid passing type args to useRef
  // (some TS configs disallow type args on untyped function calls). Use a cast instead.
  const prevNotificationRef = React.useRef(
    null,
  ) as React.MutableRefObject<NotificationData | null>;
  const [showPrompt, setShowPrompt] = useState<boolean>(() => {
    try {
      if (typeof window === "undefined") return true;
      const dismissedUntil = localStorage.getItem(BANNER_DISMISSED_KEY);
      const instructionsViewed = localStorage.getItem(INSTRUCTIONS_VIEWED_KEY) === "true";
      if ((dismissedUntil && Number(dismissedUntil) > Date.now()) || instructionsViewed) {
        return false;
      }
      return true;
    } catch (e) {
      return true;
    }
  });
  const [permissionState, setPermissionState] = useState<
    NotificationPermission | "default"
  >("default");
  const { isActiveChatId, activeChat } = useActiveChat();
  const hasShownToastRef = React.useRef(false);

  // Check if we're in mobile chat view
  const isInMobileChatView = React.useMemo(() => {
    return (
      isMobileDevice() && location.pathname === "/chats" && activeChat !== null
    );
  }, [location.pathname, activeChat]);

  // Don't show banners in admin area
  const isAdminArea = location.pathname.startsWith("/admin");

  // Check notification permission state on mount and when auth changes
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      isAuthenticated
    ) {
      const currentPermission = Notification.permission;
      setPermissionState(currentPermission);
      console.log("🔔 Notification permission state:", currentPermission);

      // Check if banner was previously dismissed
      const dismissedUntil = localStorage.getItem(BANNER_DISMISSED_KEY);
      const instructionsViewed =
        localStorage.getItem(INSTRUCTIONS_VIEWED_KEY) === "true";

      if (
        (dismissedUntil && Number(dismissedUntil) > Date.now()) ||
        instructionsViewed
      ) {
        setShowPrompt(false);
      } else {
        // Clear expired dismissal
        localStorage.removeItem(BANNER_DISMISSED_KEY);
        setShowPrompt(true);
      }
    }
  }, [isAuthenticated]);

  // Keep permissionState in sync when user returns to the tab or when storage/route changes.
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      !isAuthenticated
    )
      return;

    const syncPermission = () => {
      const currentPermission = Notification.permission;
      setPermissionState((prev) => {
        if (prev !== currentPermission) {
          if (currentPermission === "granted") {
            // If user granted notifications externally, hide any open toasts and stop prompting
            hideToast();
            setShowPrompt(false);
            hasShownToastRef.current = false;
          }
          return currentPermission;
        }
        return prev;
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") syncPermission();
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === BANNER_DISMISSED_KEY || e.key === INSTRUCTIONS_VIEWED_KEY) {
        const dismissedUntil = localStorage.getItem(BANNER_DISMISSED_KEY);
        const instructionsViewed =
          localStorage.getItem(INSTRUCTIONS_VIEWED_KEY) === "true";

        if (
          (dismissedUntil && Number(dismissedUntil) > Date.now()) ||
          instructionsViewed
        ) {
          setShowPrompt(false);
        } else {
          localStorage.removeItem(BANNER_DISMISSED_KEY);
          setShowPrompt(true);
        }
      }
    };

    window.addEventListener("focus", syncPermission);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage as any);

    return () => {
      window.removeEventListener("focus", syncPermission);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage as any);
    };
  }, [isAuthenticated, hideToast]);

  // Sync permission immediately when route changes to avoid brief false positives during navigation
  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      setPermissionState(Notification.permission);
    }
  }, [location.pathname]);

  // Handle temporary dismissal (1 week)
  const dismissBannerTemporarily = React.useCallback(() => {
    const dismissUntil = Date.now() + ONE_WEEK_IN_MS;
    localStorage.setItem(BANNER_DISMISSED_KEY, dismissUntil.toString());
    setShowPrompt(false);
    hideToast();
    hasShownToastRef.current = false;
  }, [hideToast]);

  const viewInstructions = React.useCallback(
    (event: React.MouseEvent) => {
      // First set the state to hide it immediately
      setShowPrompt(false);
      hideToast();
      hasShownToastRef.current = false;

      // Then store in localStorage that user has viewed instructions
      // This will keep the banner hidden across page navigations
      localStorage.setItem(INSTRUCTIONS_VIEWED_KEY, "true");

      // Set a timeout to remove this flag after 1 hour
      // This gives user enough time to read instructions and try them
      setTimeout(
        () => {
          localStorage.removeItem(INSTRUCTIONS_VIEWED_KEY);
        },
        60 * 60 * 1000,
      ); // 1 hour

      // Let the default navigation happen
      // We're not using preventDefault() here because we want the link to work normally
    },
    [hideToast],
  );

  // Whether to show any banner - only on Android or Chrome desktop-like browsers
  const shouldShowBanner =
    (isAndroid() || isChrome()) &&
    typeof window !== "undefined" &&
    "Notification" in window &&
    isAuthenticated &&
    showPrompt &&
    permissionState !== "granted" && // Don't show if already granted
    !isInMobileChatView && // Hide when in mobile chat view
    !isAdminArea;

  // Whether we show the denied (red) banner specifically
  const showDeniedBanner =
    shouldShowBanner && permissionState === "denied" && isAndroid();

  // Whether to show normal request banner
  const showRequestBanner = shouldShowBanner && permissionState === "default";

  // Check if toast should be shown
  const canShowToast = React.useMemo(() => {
    // Don't show if user is not authenticated
    if (!isAuthenticated) return false;

    // Don't show if permission is already granted
    if (permissionState === "granted") return false;

    return true;
  }, [isAuthenticated, permissionState]);

  // Show toast for denied permission (Android)
  React.useEffect(() => {
    // Don't show if permission is already granted
    const hasNotificationAPI =
      typeof window !== "undefined" && "Notification" in window;
    if (hasNotificationAPI && Notification.permission === "granted") return;

    if (showDeniedBanner && canShowToast && !hasShownToastRef.current) {
      hasShownToastRef.current = true;
      showToast(
        <div className="flex items-start gap-3 p-1">
          <div className="flex-shrink-0 mt-0.5">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base text-gray-900 mb-1">
              Notifiche bloccate
            </h3>
            <p className="text-sm text-gray-700 mb-2">
              Hai bloccato le notifiche per GioJoy. Se è quello che desideri,
              puoi chiudere questo messaggio.
            </p>
            <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded mb-3">
              <p className="text-sm font-medium text-gray-900 mb-2">
                Vuoi riattivare le notifiche?
              </p>
              <a
                className="inline-flex items-center justify-center gap-1.5 bg-red-500 text-white font-semibold px-4 py-2 rounded-lg shadow-sm hover:bg-red-600 transition-colors text-sm"
                href={FAQ_ROUTE + "#notifiche-android"}
                onClick={viewInstructions}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                Segui le istruzioni
              </a>
            </div>
          </div>
          <button
            onClick={dismissBannerTemporarily}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Chiudi per una settimana"
            title="Nascondi questo messaggio per una settimana"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>,
        { duration: "infinite", interactive: true },
      );
    }
  }, [
    showDeniedBanner,
    canShowToast,
    showToast,
    hideToast,
    viewInstructions,
    dismissBannerTemporarily,
  ]);

  // Show toast for permission request
  React.useEffect(() => {
    // Don't show if permission is already granted
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "granted"
    )
      return;

    if (showRequestBanner && canShowToast && !hasShownToastRef.current) {
      hasShownToastRef.current = true;
      showToast(
        <div className="flex items-start gap-3 p-1">
          <div className="flex-shrink-0 mt-0.5">
            <BellIcon className="h-6 w-6 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base text-gray-900 mb-1">
              Attiva le notifiche
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              Resta in contatto con gli organizzatori e gli altri partecipanti
              degli eventi.
            </p>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  console.log(
                    "🔔 Button clicked, calling requestPushPermission immediately...",
                  );

                  // Call requestPushPermission FIRST to preserve user gesture
                  const result = await requestPushPermission();
                  console.log("Permission result:", result);

                  // Update permission state immediately
                  const currentPermission = Notification.permission;
                  setPermissionState(currentPermission);

                  // Hide the toast and update state
                  hideToast();
                  hasShownToastRef.current = false;

                  // If permission is granted, hide the prompt permanently
                  if (currentPermission === "granted") {
                    setShowPrompt(false);
                  }
                  // If denied, the denied toast will show via the useEffect
                  // If still default (user dismissed dialog), toast will show again after 1 week
                }}
                className="flex-1 bg-blue-500 text-white font-semibold px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm shadow-sm"
              >
                Accetto
              </button>
              <button
                onClick={dismissBannerTemporarily}
                className="flex-1 bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Più tardi
              </button>
            </div>
          </div>
          <button
            onClick={dismissBannerTemporarily}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Chiudi per una settimana"
            title="Nascondi questo messaggio per una settimana"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>,
        { duration: "infinite", interactive: true },
      );
    }
  }, [
    showRequestBanner,
    canShowToast,
    showToast,
    hideToast,
    requestPushPermission,
    dismissBannerTemporarily,
  ]);

  // Reset hasShownToastRef when conditions change
  React.useEffect(() => {
    if (!shouldShowBanner) {
      hasShownToastRef.current = false;
    }
  }, [shouldShowBanner]);

  const shouldShowNotification = React.useCallback(
    (notification: any) => {
      // First check if notification is relevant
      // Don't show if I'm the sender
      if (notification.identityId === identityId) {
        return false;
      }

      // Don't show if it's for the active chat
      if (notification.chatId && isActiveChatId(notification.chatId)) {
        return false;
      }

      // Do show in all other cases (when notification isn't from me and isn't for active chat)
      return true;
    },
    [identityId, isActiveChatId],
  );

  React.useEffect(() => {
    if (
      notification &&
      notification !== prevNotificationRef.current &&
      shouldShowNotification(notification)
    ) {
      prevNotificationRef.current = notification;
      // Background (clicked) notifications are deep-linked by the service
      // worker; foreground ones only ever became a toast, so give the toast
      // the same destination when there is one.
      const targetPath = resolveNotificationPath(notification);
      const body = (
        <div className="flex items-start space-x-3">
          {notification.image && (
            <img
              src={notification.image}
              alt=""
              className="w-12 h-12 rounded-xl object-cover"
            />
          )}
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-[15px] leading-5 text-black">
              {notification.title}
            </h2>
            <p className="text-[13px] leading-4 text-gray-600 mt-0.5">
              {notification.body}
            </p>
          </div>
        </div>
      );

      showToast(
        targetPath ? (
          <div
            role="button"
            tabIndex={0}
            className="cursor-pointer text-left"
            onClick={() => {
              hideToast();
              navigate(targetPath);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                hideToast();
                navigate(targetPath);
              }
            }}
          >
            {body}
          </div>
        ) : (
          body
        ),
      );
    }
  }, [
    notification,
    showToast,
    hideToast,
    navigate,
    identityId,
    shouldShowNotification,
  ]);

  return null;
};
